import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { errorText } from "@/lib/api";
import { ASSETS } from "@/data/content";
import { Btn, Input } from "@/forum/ui";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ username: "", email: "", password: "", identifier: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (isLogin) await login(form.identifier, form.password);
      else await register(form.username, form.email, form.password);
      nav(loc.state?.from || "/forum");
    } catch (ex) {
      setErr(errorText(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0a0a0b] px-5 text-white" data-testid={`auth-page-${mode}`}>
      <img src={ASSETS.authBg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0b]/60 via-[#0a0a0b]/80 to-[#0a0a0b]" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(94,177,255,0.18),transparent_60%)] blur-3xl" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="relative w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-3" data-testid="auth-logo">
          <img src={ASSETS.logo} alt="BCRP" className="h-12 w-12" />
          <span className="font-display text-sm font-bold uppercase tracking-[0.2em]">Bone County Roleplay</span>
        </Link>
        <div className="rounded-[2rem] border border-white/10 bg-[#111113]/80 p-8 backdrop-blur-xl sm:p-10">
          <p className="eyebrow mb-3">Fórum</p>
          <h1 className="font-display text-3xl font-bold">{isLogin ? "Bejelentkezés" : "Regisztráció"}</h1>
          <form onSubmit={submit} className="mt-8 space-y-4">
            {isLogin ? (
              <Input value={form.identifier} onChange={set("identifier")} placeholder="E-mail vagy felhasználónév" autoComplete="username" required data-testid="auth-identifier" />
            ) : (
              <>
                <Input value={form.username} onChange={set("username")} placeholder="Felhasználónév (3–24, betű/szám/_)" autoComplete="username" required minLength={3} maxLength={24} pattern="[A-Za-z0-9_]+" data-testid="auth-username" />
                <Input type="email" value={form.email} onChange={set("email")} placeholder="E-mail cím" autoComplete="email" required data-testid="auth-email" />
              </>
            )}
            <Input type="password" value={form.password} onChange={set("password")} placeholder="Jelszó (min. 6 karakter)" autoComplete={isLogin ? "current-password" : "new-password"} required minLength={6} data-testid="auth-password" />
            {err && <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300" data-testid="auth-error">{err}</p>}
            {isLogin && (
              <p className="text-right"><Link to="/forgot-password" className="text-xs text-white/50 underline-offset-4 hover:text-white hover:underline" data-testid="auth-forgot-link">Elfelejtetted a jelszavad?</Link></p>
            )}
            <Btn type="submit" disabled={busy} className="btn-glow w-full" data-testid="auth-submit">{busy ? "…" : isLogin ? "Belépés" : "Fiók létrehozása"}</Btn>
          </form>
          <p className="mt-6 text-center text-sm text-white/50">
            {isLogin ? "Még nincs fiókod?" : "Van már fiókod?"}{" "}
            <Link to={isLogin ? "/register" : "/login"} className="text-white underline-offset-4 hover:underline" data-testid="auth-switch">{isLogin ? "Regisztrálj" : "Jelentkezz be"}</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
